import type { UserListQuery } from '@lib/input/user-list.input'
import * as React from 'react'
import { Loader2, UserPlus, Pencil, Mail, Trash2, Users, UserCheck, UserX, Wallet } from 'lucide-react'
import {
  useUsers,
  useUpdateUser,
  useDeactivateUser,
  useDeleteInvitedUser,
  useReactivateUser,
  useResendInvite,
  useManagedVenues,
  useSetManagedVenues,
  useAssignableRoles,
} from '@/hooks/useUsers'
import { useVenues } from '@/hooks/useVenues'
import { useAuth } from '@/auth/AuthContext'
import { useOrganization, useTransferBillingOwner } from '@/hooks/useOrganization'
import { Badge } from '@/components/ui/badge'
import { requestErrorMessage } from '@/helpers/apiClient'
import type { AssignableRolesDto, UserDto } from '@lib/dto/user.dto'
import { can } from '@/auth/access'
import { isOrganizationAdmin } from '@/auth/roles'
import { PersonInviteForm } from '@/components/people/PersonInviteForm'
import { RolePicker, roleReachHint } from '@/components/people/RolePicker'
import {
  managesPerson,
  personRoleKeyOf,
  personRoleLabel,
  reachOf,
  roleInputOf,
  type PersonRoleKey,
} from '@/components/people/personRole'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableActionsHead,
  TableActionsCell,
  TableEmptyRow,
} from '@/components/ui/table'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import { Pagination } from '@/components/ui/pagination'
import { serverPage } from '@/lib/pagination'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FormError } from '@/components/ui/form-error'
import { UserStatusBadge, inviteActionLabel } from '@/components/UserStatusBadge'
import { VenueAvailabilityPicker } from '@/components/VenueAvailabilityPicker'
import {
  UsersFilters,
  EMPTY_USERS_FILTERS,
  usersFiltersActive,
  type UsersFilterState,
} from '@/components/UsersFilters'
import { possessive } from '@lib/helpers/format'

/**
 * The venues a member of staff works at. Saves immediately on toggle
 * (replace-set). Only for a role that works at assigned venues.
 */
function ManagedVenuesField({
  userId,
  enabled,
}: {
  userId: string
  enabled: boolean
}) {
  const venuesQuery = useVenues()
  const managedQuery = useManagedVenues(userId, enabled)
  const setManaged = useSetManagedVenues()
  const [localIds, setLocalIds] = React.useState<string[] | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  // Seed local selection from the server once loaded; reset when user/enabled changes.
  React.useEffect(() => {
    setLocalIds(null)
    setError(null)
  }, [userId, enabled])

  const serverIds = managedQuery.data?.venueIds ?? []
  const value = localIds ?? serverIds
  const venues = (venuesQuery.data?.venues ?? []).map((v) => ({ id: v.id, name: v.name }))

  if (!enabled) return null

  if (venuesQuery.isPending || managedQuery.isPending) {
    return (
      <Field label="Venues they work at">
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading venues…
        </div>
      </Field>
    )
  }

  function onChange(next: string[]): void {
    setLocalIds(next)
    setError(null)
    setManaged.mutate(
      { userId, input: { venueIds: next } },
      {
        onError: (e) => {
          setError(requestErrorMessage(e))
          // Roll back optimistic selection to last known server set.
          setLocalIds(null)
        },
      },
    )
  }

  return (
    <Field
      label="Venues they work at"
      hint="They only see and work at the venues checked here. None selected means they see nothing yet."
    >
      <VenueAvailabilityPicker
        venues={venues}
        value={value}
        onChange={onChange}
        disabled={setManaged.isPending}
        emptyLabel="No venues assigned yet"
        summaryLabel={(n, total) => `Works at ${n} of ${total}`}
        noVenuesLabel="No venues yet. Add a venue first."
      />
      {error ? <FormError className="mt-1">{error}</FormError> : null}
    </Field>
  )
}

/** Edit a person's name, role, and (for a role at assigned venues) the venues they work at. */
function EditForm({
  user,
  roles,
  isBillingOwner,
  onDone,
}: {
  user: UserDto
  roles: AssignableRolesDto
  isBillingOwner: boolean
  onDone: () => void
}) {
  const saved = personRoleKeyOf(user, roles)
  const [name, setName] = React.useState(user.name)
  const [role, setRole] = React.useState<PersonRoleKey | null>(saved)
  const update = useUpdateUser()
  const [error, setError] = React.useState<string | null>(null)

  function save(): void {
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) return
    // The role goes only when it changed: someone may rename a person without giving their role again.
    const roleChange = role !== null && role !== saved ? roleInputOf(role) : {}
    update.mutate(
      { id: user.id, input: { name: trimmed, ...roleChange } },
      { onSuccess: onDone, onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  // Venues are chosen for a role that works at assigned venues, once they hold
  // one: a role change saves first, and a company-wide role clears them.
  const showVenues = reachOf(role, roles) === 'venues' && reachOf(saved, roles) === 'venues'

  return (
    <>
      <div className="space-y-4">
        <Field label="Name" htmlFor="edit-name">
          <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Role" htmlFor="edit-role" hint={roleReachHint(role, roles)}>
          <RolePicker id="edit-role" value={role} onChange={setRole} roles={roles} current={saved} disabled={isBillingOwner} />
        </Field>
        {isBillingOwner ? (
          <p className="text-sm text-ink-muted">
            {user.name} is the billing owner, so they stay an organization admin until billing ownership is
            transferred to someone else.
          </p>
        ) : null}
        {reachOf(role, roles) === 'venues' && reachOf(saved, roles) !== 'venues' ? (
          <p className="text-sm text-ink-muted">Save the new role first, then reopen edit to choose the venues they work at.</p>
        ) : null}
        <ManagedVenuesField userId={user.id} enabled={showVenues} />
        {error ? <FormError>{error}</FormError> : null}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button pending={update.isPending} type="button" onClick={save} disabled={name.trim() === ''}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogFooter>
    </>
  )
}

/**
 * Where a row stands on billing: whether this person is the billing owner, and
 * whether the person looking may hand billing to them.
 */
interface RowBilling {
  isOwner: boolean
  /** The viewer is the owner, and this is another active organization admin. */
  canHandTo: boolean
}

/**
 * Confirm handing billing to someone. A dialog rather than a one-click action:
 * the viewer gives up the card, the invoices and the cancel button in one go,
 * and cannot take them back without the new owner handing them over again.
 */
function TransferBillingDialog({ user, open, onOpenChange }: { user: UserDto; open: boolean; onOpenChange: (open: boolean) => void }) {
  const transfer = useTransferBillingOwner()
  const [error, setError] = React.useState<string | null>(null)

  function confirm(): void {
    setError(null)
    transfer.mutate(
      { userId: user.id },
      { onSuccess: () => onOpenChange(false), onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Make {user.name} the billing owner?</DialogTitle>
          <DialogDescription>
            They will manage the card, invoices and subscription, and billing emails will go to them. You
            will no longer be able to change billing unless they hand it back.
          </DialogDescription>
        </DialogHeader>
        {error ? <FormError>{error}</FormError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button pending={transfer.isPending} type="button" onClick={confirm}>
            Transfer billing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** One row of the users table: identity, role, status, and the actions the viewer may take on them. */
function UserRow({
  user,
  isSelf,
  billing,
  roles,
  manageable,
}: {
  user: UserDto
  isSelf: boolean
  billing: RowBilling
  roles: AssignableRolesDto
  /** The viewer may change this person (`managesPerson`); otherwise the row only reads. */
  manageable: boolean
}) {
  const [editing, setEditing] = React.useState(false)
  const [transferring, setTransferring] = React.useState(false)
  const deactivate = useDeactivateUser()
  const deleteInvited = useDeleteInvitedUser()
  const reactivate = useReactivateUser()
  const resend = useResendInvite()
  const [resent, setResent] = React.useState(false)

  /**
   * The one action that ends an account, which is a different thing depending on
   * whether the person ever arrived. An unaccepted invitation is deleted — there
   * is nothing to keep, and holding the address would stop it being re-sent to a
   * corrected one. An account in use is deactivated, reversibly.
   */
  function lifecycleAction(): RowAction {
    if (user.status === 'DEACTIVATED') {
      return {
        label: 'Reactivate',
        icon: UserCheck,
        onSelect: () => reactivate.mutate({ id: user.id }),
        disabled: reactivate.isPending,
      }
    }
    if (user.status === 'PENDING') {
      return {
        label: 'Delete invitation',
        icon: Trash2,
        destructive: true,
        onSelect: () => deleteInvited.mutate({ id: user.id }),
        disabled: deleteInvited.isPending,
      }
    }
    return {
      label: 'Deactivate',
      icon: UserX,
      destructive: true,
      onSelect: () => deactivate.mutate({ id: user.id }),
      disabled: isSelf || billing.isOwner || deactivate.isPending,
      disabledReason: deactivateBlockedReason(),
    }
  }

  function deactivateBlockedReason(): string | undefined {
    if (isSelf) return 'You can’t deactivate your own account'
    if (billing.isOwner) return 'The billing owner can’t be deactivated until billing is transferred'
    return undefined
  }

  const actions: RowAction[] = !manageable ? [] : [
    ...(user.status === 'PENDING'
      ? [
          {
            label: resent ? 'Sent' : inviteActionLabel(user.invitedAt),
            icon: Mail,
            priority: true,
            onSelect: () => resend.mutate({ id: user.id }, { onSuccess: () => setResent(true) }),
            disabled: resend.isPending || resent,
          } satisfies RowAction,
        ]
      : []),
    { label: 'Edit', icon: Pencil, priority: true, onSelect: () => setEditing(true) },
    ...(billing.canHandTo
      ? [{ label: 'Make billing owner', icon: Wallet, onSelect: () => setTransferring(true) } satisfies RowAction]
      : []),
    lifecycleAction(),
  ]

  return (
    <TableRow>
      <TableCell>
        <p className="flex items-center gap-2 text-sm font-medium">
          {user.name}
          {billing.isOwner ? <Badge tone="info">Billing owner</Badge> : null}
        </p>
        <p className="text-xs text-ink-muted">{user.email}</p>
      </TableCell>
      <TableCell className="text-sm text-ink-muted">{personRoleLabel(user)}</TableCell>
      <TableCell>
        <UserStatusBadge
          status={user.status}
          invitedAt={user.invitedAt}
          inviteExpiresAt={user.inviteExpiresAt}
        />
      </TableCell>
      <TableActionsCell>
        {actions.length > 0 ? <RowActions label={user.name} actions={actions} /> : null}
        <Dialog open={editing} onOpenChange={setEditing}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit user</DialogTitle>
              <DialogDescription>
                Update {possessive(user.name)} name and role
                {user.staffRole?.reach === 'venues' ? ', and the venues they work at' : ''}.
              </DialogDescription>
            </DialogHeader>
            {editing ? <EditForm user={user} roles={roles} isBillingOwner={billing.isOwner} onDone={() => setEditing(false)} /> : null}
          </DialogContent>
        </Dialog>
        {billing.canHandTo ? (
          <TransferBillingDialog user={user} open={transferring} onOpenChange={setTransferring} />
        ) : null}
      </TableActionsCell>
    </TableRow>
  )
}


/** The list's query for a role filter: a kind of account, or staff on one company role. */
function roleQueryOf(key: PersonRoleKey | null): Pick<UserListQuery, 'role' | 'roleId'> {
  if (key === null) return {}
  if (key === 'organizationAdmin') return { role: 'ORGANIZATION_ADMIN' }
  if (key === 'client') return { role: 'CLIENT' }
  return { roleId: key.slice('role:'.length) }
}

/**
 * The company's people: organization admins see and manage everyone; staff
 * with People View see the staff, and with People Edit invite and change them
 * within the guardrail (docs/custom-roles.spec.md, "People and the guardrail").
 */
export function UsersPage() {
  const { user } = useAuth()
  const rolesQuery = useAssignableRoles()
  const organizationAdmin = isOrganizationAdmin(user)
  const invites = can(user, 'people:edit')
  const venuesQuery = useVenues()
  const ownerId = useOrganization().data?.billingOwner?.id ?? null
  const [inviting, setInviting] = React.useState(false)
  const [filters, setFilters] = React.useState<UsersFilterState>(EMPTY_USERS_FILTERS)
  const [page, setPage] = React.useState(1)

  /**
   * The filters as the API's query.
   *
   * A venue means "a venue admin assigned to it", which is what the chip has
   * always meant — the API applies the role with it, so the two chips together
   * cannot ask for a client at a venue and get a surprise.
   */
  const query: UserListQuery = {
    ...roleQueryOf(filters.role),
    ...(filters.venueId !== null ? { venueId: filters.venueId } : {}),
  }

  const usersQuery = useUsers(query, page)

  /** Where one row stands on billing, for its badge and its "Make billing owner" action. */
  function rowBilling(row: UserDto): RowBilling {
    const viewerIsOwner = ownerId !== null && ownerId === user?.id
    return {
      isOwner: row.id === ownerId,
      canHandTo:
        viewerIsOwner && row.id !== ownerId && row.role === 'ORGANIZATION_ADMIN' && row.status === 'ACTIVE',
    }
  }

  /**
   * Filtering starts the list again.
   *
   * `pageOf` clamps, so a filter can never leave the view on a page that no
   * longer exists — but landing on page 2 of results you have just asked for is
   * not what anybody means by filtering.
   */
  function filterTo(next: UsersFilterState): void {
    setFilters(next)
    setPage(1)
  }

  const venues = React.useMemo(
    () =>
      (venuesQuery.data?.venues ?? [])
        .map((v) => ({ id: v.id, name: v.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [venuesQuery.data],
  )

  /** The band's label: what the list holds, since the page title says the rest. */
  function userCount(count: number): string {
    return `${String(count)} ${count === 1 ? 'person' : 'people'}`
  }

  /**
   * The action that adds to this table, in the table's own bar after the
   * filters.
   *
   * Primary, as "New event" is on the events list: Plan View gives a list
   * page's one main action the fill. It was ghost on the reasoning that the
   * page's primary button is the one inside the invite dialog, but the dialog
   * is its own layer, with its own one primary.
   */
  function inviteAction(): React.ReactElement | null {
    if (!invites) return null
    return (
      <Button size="sm" onClick={() => setInviting(true)}>
        <UserPlus className="h-4 w-4" /> Invite user
      </Button>
    )
  }

  function renderContent() {
    if (usersQuery.isPending || rolesQuery.isPending) return <LoadingState label="Loading users…" />
    if (usersQuery.isError) {
      return <ErrorState message={requestErrorMessage(usersQuery.error)} onRetry={() => void usersQuery.refetch()} />
    }
    if (rolesQuery.isError) {
      return <ErrorState message={requestErrorMessage(rolesQuery.error)} onRetry={() => void rolesQuery.refetch()} />
    }
    const roles = rolesQuery.data
    const list = usersQuery.data
    const users = list.users
    const filtering = usersFiltersActive(filters)

    // `total` is what the filters matched. Unfiltered, nothing matched means
    // nobody is here yet, and the useful thing is the invite. Filtered, the
    // table stays so its bar — where the filters are — stays in reach.
    if (list.total === 0 && !filtering) {
      return (
        <EmptyState
          icon={Users}
          title="No users yet"
          description={invites ? 'Invite someone to join your organization.' : 'Nobody is on the company’s staff yet.'}
          action={
            invites ? (
              <Button type="button" size="sm" onClick={() => setInviting(true)}>
                <UserPlus className="h-4 w-4" /> Invite user
              </Button>
            ) : undefined
          }
        />
      )
    }

    // Filtered and sliced by the API; `serverPage` is only the readout's
    // arithmetic.
    const shown = serverPage(page, list.total, users.length)
    return (
        <Table
          heading={filtering ? `${String(list.total)} matching` : userCount(list.total)}
          actions={
            <>
              <UsersFilters filters={filters} onChange={filterTo} venues={venues} roles={roles} />
              {inviteAction()}
            </>
          }
          footer={
            <Pagination
              page={shown.page}
              pageCount={shown.pageCount}
              from={shown.from}
              to={shown.to}
              total={shown.total}
              unit="people"
              onPage={setPage}
            />
          }
        >
          <TableHeader>
            <TableHead>User</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableActionsHead />
          </TableHeader>
          <TableBody>
            {list.total === 0 ? (
              <TableEmptyRow colSpan={4}>
                No users match these filters.{' '}
                <button
                  type="button"
                  onClick={() => filterTo(EMPTY_USERS_FILTERS)}
                  className="underline underline-offset-2 outline-none transition-colors hover:text-ink focus-visible:focus-ds"
                >
                  Clear filters
                </button>
              </TableEmptyRow>
            ) : (
              users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === user?.id}
                  billing={rowBilling(u)}
                  roles={roles}
                  // A company-wide page: everyone on it works where the viewer does.
                  manageable={managesPerson(user, u, roles, true)}
                />
              ))
            )}
          </TableBody>
        </Table>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader
        title="Users"
        description={organizationAdmin ? 'Invite people to your organization and manage their access.' : 'The company’s staff and their roles.'}
      />
      {renderContent()}
      <Dialog open={inviting} onOpenChange={setInviting}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
            <DialogDescription>We’ll email them a link to set a password and join.</DialogDescription>
          </DialogHeader>
          {inviting && rolesQuery.data !== undefined ? <PersonInviteForm roles={rolesQuery.data} onDone={() => setInviting(false)} /> : null}
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
